import { useEffect, useRef } from "react";

function random(min, max) {
  return Math.random() * (max - min) + min;
}

function clamp(min, value, max) {
  return Math.min(max, Math.max(min, value));
}

function createStar(bounds) {
  const star = document.createElement("span");
  star.className = "starfield__star";

  const sizeRoll = Math.random();
  const size =
    sizeRoll < 0.58 ? 1 : sizeRoll < 0.82 ? 1.5 : sizeRoll < 0.94 ? 2 : 3;
  const left = random(0, bounds.width);
  const top = random(0, bounds.height);
  const opacity = size >= 2 ? random(0.65, 1) : random(0.45, 0.92);

  star.style.left = `${left}px`;
  star.style.top = `${top}px`;
  star.style.width = `${size}px`;
  star.style.height = `${size}px`;
  star.style.opacity = `${opacity}`;

  if (size >= 2) {
    star.classList.add("starfield__star--big");
  }

  if (Math.random() < 0.12) {
    const twinkle = Math.floor(random(1, 5));
    const duration = random(3.8, 7.5);
    const delay = random(0, 6);
    star.style.animation = `starfieldTwinkle${twinkle} ${duration}s ${delay}s infinite`;
  }

  return star;
}

function setCometStyle(comet, bounds, variant) {
  const isRain = variant === "rain";

  // Default: fall diagonally down-left (meteor shower). Rain variant: tighter angle and faster cadence.
  const startX = isRain
    ? random(bounds.width * -0.05, bounds.width * 1.05)
    : random(bounds.width * 0.55, bounds.width * 1.1);
  const startY = random(-bounds.height * 0.3, bounds.height * 0.12);

  const travelX = isRain
    ? -random(bounds.width * 0.04, bounds.width * 0.12)
    : -random(bounds.width * 0.18, bounds.width * 0.42);
  const travelY = isRain
    ? random(bounds.height * 1.05, bounds.height * 1.55)
    : random(bounds.height * 0.9, bounds.height * 1.25);

  const duration = isRain ? random(1.15, 2.1) : random(2.8, 4.8);
  const delay = isRain ? random(0, 2.2) : random(0, 8.5);

  const angle = (Math.atan2(travelY, travelX) * 180) / Math.PI;

  comet.style.left = `${startX}px`;
  comet.style.top = `${startY}px`;
  comet.style.setProperty("--tx", `${travelX}px`);
  comet.style.setProperty("--ty", `${travelY}px`);
  comet.style.setProperty("--angle", `${angle}deg`);
  comet.style.setProperty("--duration", `${duration}s`);
  comet.style.setProperty("--delay", `${delay}s`);
}

export default function StarryBackground({
  className = "",
  variant = "night",
  showStars = true,
  showComets = true,
  starDensity = null,
  minStars = null,
  maxStars = null,
  cometCount = null,
} = {}) {
  const starsRef = useRef(null);
  const cometsRef = useRef(null);

  useEffect(() => {
    const starsLayer = starsRef.current;
    const cometsLayer = cometsRef.current;
    if (!starsLayer || !cometsLayer) return;

    const reducedMotionQuery = window.matchMedia?.(
      "(prefers-reduced-motion: reduce)",
    );
    let prefersReducedMotion = Boolean(reducedMotionQuery?.matches);

    let cometCleanups = [];
    let resizeTimer = null;

    const buildStars = (bounds) => {
      starsLayer.replaceChildren();
      if (!showStars) return;

      // Keep this intentionally light: too many DOM stars tanks perf on low-end GPUs.
      const density =
        typeof starDensity === "number" && Number.isFinite(starDensity)
          ? Math.max(1, starDensity)
          : prefersReducedMotion
            ? 24000
            : 32000;
      const targetCount = Math.floor((bounds.width * bounds.height) / density);
      const minCount =
        typeof minStars === "number" && Number.isFinite(minStars)
          ? Math.max(0, Math.floor(minStars))
          : 80;
      const maxCount =
        typeof maxStars === "number" && Number.isFinite(maxStars)
          ? Math.max(minCount, Math.floor(maxStars))
          : 180;
      const starCount = clamp(minCount, targetCount, maxCount);

      const frag = document.createDocumentFragment();
      for (let i = 0; i < starCount; i += 1) {
        const star = createStar(bounds);
        frag.appendChild(star);
      }
      starsLayer.appendChild(frag);
    };

    const buildComets = (bounds) => {
      cometCleanups.forEach((fn) => fn());
      cometCleanups = [];
      cometsLayer.replaceChildren();

      if (!showComets) return;
      if (prefersReducedMotion) return;

      const resolvedCometCount =
        typeof cometCount === "number" && Number.isFinite(cometCount)
          ? Math.max(0, Math.floor(cometCount))
          : variant === "rain"
            ? clamp(2, Math.floor(bounds.width / 260), 7)
            : clamp(1, Math.floor(bounds.width / 700), 3);

      for (let i = 0; i < resolvedCometCount; i += 1) {
        const comet = document.createElement("span");
        comet.className = "starfield__comet";

        const restart = (nextBounds) => {
          setCometStyle(comet, nextBounds, variant);
          comet.style.animation = "none";
          void comet.offsetWidth;
          comet.style.animation = `starfieldCometFall var(--duration) linear var(--delay)`;
        };

        const onEnd = () => {
          restart({ width: window.innerWidth, height: window.innerHeight });
        };

        comet.addEventListener("animationend", onEnd);
        cometCleanups.push(() => comet.removeEventListener("animationend", onEnd));

        cometsLayer.appendChild(comet);
        restart(bounds);
      }
    };

    const buildAll = () => {
      const bounds = { width: window.innerWidth, height: window.innerHeight };
      buildStars(bounds);
      buildComets(bounds);
    };

    const onResize = () => {
      if (resizeTimer) window.clearTimeout(resizeTimer);
      resizeTimer = window.setTimeout(buildAll, 200);
    };

    const onReducedMotionChange = () => {
      prefersReducedMotion = Boolean(reducedMotionQuery?.matches);
      buildAll();
    };

    window.addEventListener("resize", onResize, { passive: true });
    reducedMotionQuery?.addEventListener?.("change", onReducedMotionChange);

    buildAll();

    return () => {
      if (resizeTimer) window.clearTimeout(resizeTimer);
      window.removeEventListener("resize", onResize);
      reducedMotionQuery?.removeEventListener?.("change", onReducedMotionChange);
      cometCleanups.forEach((fn) => fn());
      starsLayer.replaceChildren();
      cometsLayer.replaceChildren();
    };
  }, [showStars, showComets, variant]);

  const classNames = ["starfield", className].filter(Boolean);
  if (variant && variant !== "night") classNames.push(`starfield--${variant}`);

  const style =
    variant === "rain"
      ? {
          "--comet-start": "rgba(245, 250, 255, 0.92)",
          "--comet-end": "rgba(190, 210, 225, 0.86)",
          "--comet-glow": "rgba(210, 220, 230, 0.22)",
          "--comet-glow-secondary": "rgba(140, 155, 170, 0.18)",
        }
      : undefined;

  return (
    <div className={classNames.join(" ")} style={style} aria-hidden="true">
      <div className="starfield__stars" ref={starsRef} />
      <div className="starfield__comets" ref={cometsRef} />
      <div className="starfield__haze" />
    </div>
  );
}
